import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChevronDownIcon, ChevronRightIcon, DocumentIcon, PencilIcon, PlusIcon,
  Squares2X2Icon, TableCellsIcon, TrashIcon,
} from '@heroicons/react/24/outline';
import { MAX_TABLES, allFields, newId } from '../../lib/agentShared/formSchema';
import type {
  Condition, FieldOption, FormField, FormSchemaV2, FormSection, PageBreak, SectionKind,
} from '../../lib/agentShared/formSchema';
import {
  AddNewMenu, LogicComposer, OptionChip, RelationsEditor, ResponseSetModal, SliderModal,
  TriggerMenu, TYPE_ICONS, TYPE_LABELS, TypePickerPopover, opLabel,
} from './editorParts';
import type { TriggerKind } from './editorParts';
import { Button, Checkbox, IconButton, Input, SegmentedControl } from '../ui';

// Story 4-110 — the three-way page-flow picker's option list, shared by
// every section's per-row control below (one source, not re-typed per call
// site). Labels are the SHORT form (the control sits in an already-crowded
// header row, same space budget as the "Repeat this section" checkbox
// beside it) — the fuller explanation lives in the wrapping element's own
// `title`, same pattern the "Repeat this section" checkbox beside it uses.
const PAGE_BREAK_OPTIONS: Array<{ value: PageBreak; label: string }> = [
  { value: 'auto', label: 'Auto' },
  { value: 'always', label: 'Break before' },
  { value: 'avoid', label: 'Keep with previous' },
];

// High-fidelity, drag-sortable template canvas (SafetyCulture-style).
// Questions can NEST: an "Ask questions" trigger on a logic rule inserts a
// follow-up question whose visibleWhen carries the rule's condition; nested
// questions render indented beneath the rule. Response sets and sliders are
// edited in modals; the "+ Question / Section" rail docks beside the
// selected row. Controlled: parent owns the schema.
interface Props {
  schema: FormSchemaV2;
  onChange: (schema: FormSchemaV2) => void;
  /** Validation messages keyed by field id — rows get highlighted with a tooltip. */
  fieldErrors?: Record<string, string[]>;
  /** When set, the canvas selects + scrolls to this field (e.g. first invalid on save). */
  highlightField?: string | null;
  /** Optional external selection used by the workspace outline/inspector. */
  focusedSectionId?: string | null;
  focusedFieldId?: string | null;
  onFocusChange?: (sectionId: string, fieldId: string | null) => void;
  /** Hide the legacy viewport-fixed add rail when hosted inside a document studio. */
  floatingAddRail?: boolean;
}

type DragPayload =
  | { kind: 'section'; sectionId: string }
  | { kind: 'field'; sectionId: string; fieldId: string };

const keyFromLabel = (label: string) =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 50) || `q_${Date.now() % 10_000}`;

const condEq = (a: Condition, b: Condition) =>
  a.field === b.field && a.op === b.op && String(a.value ?? '') === String(b.value ?? '');

const scrollInsideNearestRegion = (element: HTMLElement | undefined, block: 'start' | 'center'): void => {
  if (!element) return;
  let scroller = element.parentElement;
  while (scroller && scroller !== document.body) {
    const overflowY = window.getComputedStyle(scroller).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && scroller.scrollHeight > scroller.clientHeight) break;
    scroller = scroller.parentElement;
  }
  if (!scroller || scroller === document.body) {
    element.scrollIntoView({ behavior: 'smooth', block });
    return;
  }

  const elementRect = element.getBoundingClientRect();
  const scrollerRect = scroller.getBoundingClientRect();
  const offset = block === 'center'
    ? (scroller.clientHeight - elementRect.height) / 2
    : 0;
  scroller.scrollTo({
    top: Math.max(0, scroller.scrollTop + elementRect.top - scrollerRect.top - offset),
    behavior: 'smooth',
  });
};

export function EditorCanvas({
  schema,
  onChange,
  fieldErrors,
  highlightField,
  focusedSectionId,
  focusedFieldId,
  onFocusChange,
  floatingAddRail = true,
}: Props): JSX.Element {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [typePickerFor, setTypePickerFor] = useState<{ fieldId: string; anchor: HTMLElement } | null>(null);
  const [logicComposerFor, setLogicComposerFor] = useState<string | null>(null);
  const [detailsFor, setDetailsFor] = useState<string | null>(null);
  const [responseModal, setResponseModal] = useState<{ fieldId: string; options: FieldOption[] } | null>(null);
  const [sliderModal, setSliderModal] = useState<string | null>(null);
  const [triggerMenuFor, setTriggerMenuFor] = useState<{ fieldId: string; ruleIndex: number; anchor: HTMLElement } | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(schema.sections[0]?.id ?? null);
  const [addMenu, setAddMenu] = useState<{ anchor: HTMLElement; sectionId: string | null } | null>(null);
  const [railTop, setRailTop] = useState<number | null>(null);
  const dragRef = useRef<DragPayload | null>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());

  const update = (mutate: (draft: FormSchemaV2) => void) => {
    const next: FormSchemaV2 = JSON.parse(JSON.stringify(schema));
    mutate(next);
    onChange(next);
  };

  const fieldKeys = allFields(schema).map((f) => f.key);
  const findFieldById = (id: string) => allFields(schema).find((f) => f.id === id);
  // Human name for a field referenced in a condition (label preferred over key).
  const refName = (ref: string) => {
    const f = allFields(schema).find((x) => x.key === ref || x.id === ref);
    return f?.label?.trim() || f?.key || ref;
  };

  // Jump to the field the parent wants highlighted (e.g. first invalid on save).
  useEffect(() => {
    if (!highlightField) return;
    setSelected(highlightField);
    const el = rowRefs.current.get(highlightField);
    scrollInsideNearestRegion(el, 'center');
  }, [highlightField]);

  useEffect(() => {
    if (focusedFieldId) {
      setSelected(focusedFieldId);
      if (focusedSectionId) setActiveSectionId(focusedSectionId);
      scrollInsideNearestRegion(rowRefs.current.get(focusedFieldId), 'center');
      return;
    }
    if (focusedSectionId) {
      setSelected(null);
      setActiveSectionId(focusedSectionId);
      scrollInsideNearestRegion(sectionRefs.current.get(focusedSectionId), 'start');
      return;
    }
    setSelected(null);
  }, [focusedFieldId, focusedSectionId]);

  const patchField = (fieldId: string, patch: { [K in keyof FormField]?: FormField[K] | undefined }) =>
    update((d) => {
      for (const s of d.sections) {
        const f = s.fields.find((x) => x.id === fieldId);
        if (!f) continue;
        const oldKey = f.key;
        Object.assign(f, patch);
        // A key rename must follow every reference: conditions on any field
        // (logic rules + nesting visibleWhen) and cross-field relations.
        if (patch.key && patch.key !== oldKey) {
          for (const sec of d.sections) {
            for (const other of sec.fields) {
              for (const cond of other.visibleWhen ?? []) {
                if (cond.field === oldKey) cond.field = patch.key;
              }
              for (const rule of other.requirements ?? []) {
                if (rule.when.field === oldKey) rule.when.field = patch.key;
              }
            }
          }
          d.relations = d.relations.map((r) => (r.type === 'dependsOn'
            ? {
              ...r,
              field: r.field === oldKey ? patch.key! : r.field,
              on: r.on === oldKey ? patch.key! : r.on,
            }
            : { ...r, fields: r.fields.map((k) => (k === oldKey ? patch.key! : k)) }));
        }
      }
    });

  // ---- nesting helpers: a field is nested under the question its
  // visibleWhen condition references (SC's "Ask questions" trigger) ----
  const parentOf = (section: FormSection, f: FormField) =>
    section.fields.find((p) => p.id !== f.id && (f.visibleWhen ?? []).some((c) => c.field === p.key || c.field === p.id));
  const childrenOf = (section: FormSection, parent: FormField) =>
    section.fields.filter((f) => f.id !== parent.id && (f.visibleWhen ?? []).some((c) => c.field === parent.key || c.field === parent.id));

  const addQuestion = (opts: { sectionId?: string | null | undefined; afterId?: string | undefined; visibleWhen?: Condition[] }) => {
    const targetId = opts.sectionId ?? activeSectionId ?? schema.sections[schema.sections.length - 1]?.id;
    if (!targetId) return null;
    const id = newId();
    update((d) => {
      const section = d.sections.find((s) => s.id === targetId);
      if (!section) return;
      const n = allFields(d).length + 1;
      let key = `question_${n}`;
      while (allFields(d).some((f) => f.key === key)) key = `${key}_x`;
      const field: FormField = { id, key, label: '', type: 'text' };
      if (opts.visibleWhen) field.visibleWhen = opts.visibleWhen;
      let at = section.fields.length;
      if (opts.afterId) {
        const idx = section.fields.findIndex((f) => f.id === opts.afterId);
        const anchor = section.fields[idx];
        if (idx >= 0 && anchor) {
          at = idx + 1;
          // skip past the anchor's existing nested block
          while (at < section.fields.length
            && (section.fields[at]?.visibleWhen ?? []).some((c) => c.field === anchor.key || c.field === anchor.id)) {
            at += 1;
          }
        }
      }
      section.fields.splice(at, 0, field);
    });
    setSelected(id);
    onFocusChange?.(targetId, id);
    return id;
  };

  const addSection = (kind: SectionKind = 'section') => {
    const id = newId();
    update((d) => {
      const names: Record<SectionKind, string> = {
        section: `Section ${d.sections.length + 1}`,
        page: 'Untitled Page',
        table: 'Untitled Table',
      };
      d.sections.push({
        id,
        name: names[kind],
        fields: [],
        ...(kind !== 'section' ? { kind } : {}),
      });
    });
    setActiveSectionId(id);
    onFocusChange?.(id, null);
  };

  // ---- rail docks beside the selected row ----
  const positionRail = useCallback(() => {
    const el = selected ? rowRefs.current.get(selected) : null;
    if (!el) { setRailTop(null); return; }
    const rect = el.getBoundingClientRect();
    setRailTop(Math.min(Math.max(rect.top, 90), window.innerHeight - 160));
  }, [selected]);

  useEffect(() => {
    positionRail();
    window.addEventListener('scroll', positionRail, { passive: true });
    window.addEventListener('resize', positionRail);
    return () => {
      window.removeEventListener('scroll', positionRail);
      window.removeEventListener('resize', positionRail);
    };
  }, [positionRail, schema]);

  // ---- native drag & drop reordering ----
  const onDropField = (targetSectionId: string, targetIndex: number) => {
    const drag = dragRef.current;
    if (!drag || drag.kind !== 'field') return;
    update((d) => {
      const from = d.sections.find((s) => s.id === drag.sectionId);
      const to = d.sections.find((s) => s.id === targetSectionId);
      if (!from || !to) return;
      const i = from.fields.findIndex((f) => f.id === drag.fieldId);
      if (i < 0) return;
      const [moved] = from.fields.splice(i, 1);
      if (!moved) return;
      const insertAt = from === to && i < targetIndex ? targetIndex - 1 : targetIndex;
      to.fields.splice(Math.max(0, Math.min(insertAt, to.fields.length)), 0, moved);
    });
    dragRef.current = null;
  };

  const onDropSection = (targetIndex: number) => {
    const drag = dragRef.current;
    if (!drag || drag.kind !== 'section') return;
    update((d) => {
      const i = d.sections.findIndex((s) => s.id === drag.sectionId);
      if (i < 0) return;
      const [moved] = d.sections.splice(i, 1);
      if (!moved) return;
      const insertAt = i < targetIndex ? targetIndex - 1 : targetIndex;
      d.sections.splice(Math.max(0, Math.min(insertAt, d.sections.length)), 0, moved);
    });
    dragRef.current = null;
  };

  const allowDrop = (e: React.DragEvent) => e.preventDefault();

  const handle = (payload: DragPayload) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      dragRef.current = payload;
      e.dataTransfer.effectAllowed = 'move';
      e.stopPropagation();
    },
  });

  const applyTrigger = (field: FormField, section: FormSection, ruleIndex: number, kind: TriggerKind) => {
    if (kind === 'askQuestions') {
      const rule = field.requirements?.[ruleIndex];
      if (!rule) return;
      addQuestion({ sectionId: section.id, afterId: field.id, visibleWhen: [{ ...rule.when }] });
      return;
    }
    update((d) => {
      const f = d.sections.flatMap((s) => s.fields).find((x) => x.id === field.id);
      const rule = f?.requirements?.[ruleIndex];
      if (rule && !rule.effects.includes(kind)) rule.effects.push(kind);
    });
  };

  const typeCell = (field: FormField) => {
    if (['select', 'multiselect'].includes(field.type) && field.options?.length) {
      return (
        <span className="flex items-center gap-1 overflow-hidden">
          {field.options.slice(0, 2).map((o) => <OptionChip key={o.id ?? o.label} option={o} small />)}
          {field.options.length > 2 && (
            <span className="text-caption text-muted-foreground">+{field.options.length - 2}</span>
          )}
        </span>
      );
    }
    if (field.type === 'rating') {
      return (
        <span className="flex items-center gap-2 text-xs text-foreground">
          {TYPE_ICONS.rating}
          Slider {field.min ?? 1}–{field.max ?? 10}
        </span>
      );
    }
    return (
      <span className="flex items-center gap-2 text-xs text-foreground">
        {TYPE_ICONS[field.type]}
        {TYPE_LABELS[field.type]}
      </span>
    );
  };

  const effectLabel = (e: string) =>
    e === 'require' ? 'require answer' : e.replace('require', 'require ').toLowerCase();

  // ------------------------------------------------------------------
  // depth/visited: nesting recurses (a follow-up question can have its own
  // "Ask questions" follow-ups); visited guards against reference cycles.
  const renderFieldRow = (section: FormSection, field: FormField, depth: number, visited: Set<string>): JSX.Element => {
    const nested = depth > 0;
    const isSelected = selected === field.id;
    const children = childrenOf(section, field).filter((c) => !visited.has(c.id));
    const nestParent = nested ? parentOf(section, field) : undefined;
    const isChoice = ['select', 'multiselect'].includes(field.type);
    const errors = fieldErrors?.[field.id];

    return (
      <div
        key={field.id}
        data-template-field-id={field.id}
        style={nested ? { paddingLeft: Math.min(depth, 3) * 24, boxShadow: 'inset 3px 0 0 0 #f59e0b' } : undefined}
      >
        {/* main row */}
        <div
          ref={(el) => { if (el) rowRefs.current.set(field.id, el); else rowRefs.current.delete(field.id); }}
          onDragOver={allowDrop}
          onDrop={(e) => {
            e.stopPropagation();
            onDropField(section.id, section.fields.findIndex((f) => f.id === field.id));
          }}
          className={`group flex items-center gap-2 border-t border-border/30 px-4 py-2 transition ${
            errors
              ? 'bg-destructive/5 ring-1 ring-inset ring-destructive/60'
              : isSelected
                ? 'bg-primary/5 ring-1 ring-inset ring-primary/40'
                : 'hover:bg-white/40 dark:hover:bg-white/5'
          }`}
          onClick={(event) => {
            event.stopPropagation();
            const next = isSelected ? null : field.id;
            setSelected(next);
            setActiveSectionId(section.id);
            onFocusChange?.(section.id, next);
          }}
        >
          {errors && (
            <span
              className="flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded-full bg-destructive text-caption font-bold text-white"
              title={errors.join('\n')}
            >
              i
            </span>
          )}
          <span
            {...handle({ kind: 'field', sectionId: section.id, fieldId: field.id })}
            className="cursor-grab select-none text-muted-foreground opacity-40 group-hover:opacity-100"
            title="Drag to reorder"
          >
            ⠿
          </span>
          {field.required && <span className="text-sm font-bold text-destructive" title="Required">*</span>}
          <Input
            bare
            className={`text-sm ${field.type === 'instruction' ? 'italic text-muted-foreground' : 'text-foreground'}`}
            placeholder={section.kind === 'table' ? 'Type column header' : 'Type question'}
            value={field.label}
            onClick={(e) => e.stopPropagation()}
            onFocus={() => {
              setSelected(field.id);
              setActiveSectionId(section.id);
              onFocusChange?.(section.id, field.id);
            }}
            onChange={(e) => {
              const patch: Partial<FormField> = { label: e.target.value };
              // Keep deriving the key while it is still auto-generated (matches
              // the previous label's derivation or the question_N placeholder);
              // a manual key edit in "Edit details" breaks the link.
              const autoKey = field.key.startsWith('question_')
                || field.key.startsWith('q_')
                || field.key === keyFromLabel(field.label);
              if (autoKey) {
                const derived = keyFromLabel(e.target.value);
                if (derived && !fieldKeys.filter((k) => k !== field.key).includes(derived)) {
                  patch.key = derived;
                }
              }
              patchField(field.id, patch);
            }}
          />
          <div className="relative flex w-56 items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="fit"
              className="min-w-0 flex-1 shrink rounded-lg border border-transparent px-2 py-1.5 hover:border-border"
              onClick={(e) => setTypePickerFor(
                typePickerFor?.fieldId === field.id ? null : { fieldId: field.id, anchor: e.currentTarget },
              )}
            >
              <span className="flex min-w-0 items-center justify-between gap-2">
                {typeCell(field)}
                <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </span>
            </Button>
            {isChoice && (
              <IconButton
                icon={<PencilIcon className="h-3.5 w-3.5" />}
                variant="ghost"
                size="sm"
                className="text-muted-foreground opacity-0 hover:text-primary group-hover:opacity-100"
                title="Edit responses"
                aria-label="Edit responses"
                onClick={() => setResponseModal({ fieldId: field.id, options: field.options ?? [] })}
              />
            )}
            {field.type === 'rating' && (
              <IconButton
                icon={<PencilIcon className="h-3.5 w-3.5" />}
                variant="ghost"
                size="sm"
                className="text-muted-foreground opacity-0 hover:text-primary group-hover:opacity-100"
                title="Edit slider range"
                aria-label="Edit slider range"
                onClick={() => setSliderModal(field.id)}
              />
            )}
            {typePickerFor?.fieldId === field.id && (
              <TypePickerPopover
                anchor={typePickerFor.anchor}
                onPick={(patch) => {
                  patchField(field.id, patch);
                  if (patch.type === 'rating') setSliderModal(field.id);
                }}
                onEditSet={(options) => setResponseModal({ fieldId: field.id, options })}
                onClose={() => setTypePickerFor(null)}
              />
            )}
          </div>
          <IconButton
            icon={<TrashIcon className="h-4 w-4" />}
            variant="ghost"
            size="sm"
            className="text-muted-foreground opacity-0 hover:text-destructive group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              update((d) => {
                const s = d.sections.find((x) => x.id === section.id);
                if (s) s.fields = s.fields.filter((f) => f.id !== field.id);
                d.relations = d.relations.filter((r) =>
                  r.type === 'dependsOn' ? r.field !== field.key && r.on !== field.key : !r.fields.includes(field.key));
              });
            }}
            aria-label="Delete question"
          />
        </div>

        {errors && (
          <p className="border-t border-border/20 bg-destructive/5 px-10 py-1 text-label font-medium text-destructive">
            {errors[0]}{errors.length > 1 ? ` (+${errors.length - 1} more)` : ''}
          </p>
        )}

        {/* settings strip */}
        {isSelected && (
          <div className="flex flex-wrap items-center gap-4 border-t border-border/30 bg-white/30 px-10 py-2 text-xs dark:bg-white/5">
            <Button
              variant="ghost"
              size="fit"
              className="font-semibold text-primary hover:bg-transparent"
              onClick={() => setLogicComposerFor(logicComposerFor === field.id ? null : field.id)}
            >
              ⚡ Add logic
            </Button>
            <label className="flex items-center gap-1.5 text-muted-foreground">
              <Checkbox
                value={Boolean(field.required)}
                onChange={(e) => patchField(field.id, { required: e.target.checked })}
              />
              Required
            </label>
            {/* Story 4-117 — "include/exclude internal notes": a capture-schema
                property, same plain `patchField` mutation `required` above
                already uses (Build-tab authoring is not part of Report
                Studio's typed-command system — see `FormField.internalOnly`'s
                own `formSchema.d.ts` comment). The report-wide
                `includeInternalNotes` toggle (Report tab -> Content) decides
                whether a field marked here actually renders. */}
            <label
              className="flex items-center gap-1.5 text-muted-foreground"
              title="Captured normally, but excluded from the report unless the report's Content tab opts internal notes in"
            >
              <Checkbox
                value={Boolean(field.internalOnly)}
                onChange={(e) => patchField(field.id, { internalOnly: e.target.checked })}
              />
              Internal note
            </label>
            {isChoice && (
              <>
                <label className="flex items-center gap-1.5 text-muted-foreground">
                  <Checkbox
                    value={field.type === 'multiselect'}
                    onChange={(e) => patchField(field.id, { type: e.target.checked ? 'multiselect' : 'select' })}
                  />
                  Multiple selection
                </label>
                <Button
                  variant="ghost"
                  size="fit"
                  className="font-semibold text-muted-foreground hover:text-primary hover:bg-transparent"
                  onClick={() => setResponseModal({ fieldId: field.id, options: field.options ?? [] })}
                >
                  Edit responses
                </Button>
              </>
            )}
            {field.type === 'rating' && (
              <Button
                variant="ghost"
                size="fit"
                className="font-semibold text-muted-foreground hover:text-primary hover:bg-transparent"
                onClick={() => setSliderModal(field.id)}
              >
                Edit slider
              </Button>
            )}
            <Button
              variant="ghost"
              size="fit"
              className="font-semibold text-muted-foreground hover:text-primary hover:bg-transparent"
              onClick={() => setDetailsFor(detailsFor === field.id ? null : field.id)}
            >
              {detailsFor === field.id ? 'Hide details' : 'Edit details'}
            </Button>
            {nested && nestParent && (
              <span className="text-caption text-amber-600 dark:text-amber-400">
                nested under {nestParent.label || nestParent.key}
              </span>
            )}
            <span className="ml-auto font-mono text-caption text-muted-foreground">{field.key}</span>
          </div>
        )}

        {/* details editor */}
        {isSelected && detailsFor === field.id && (
          <div className="space-y-3 border-t border-border/30 bg-white/30 px-10 py-4 dark:bg-white/5">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              key
              <Input
                size="sm"
                className="w-48 font-mono"
                value={field.key}
                onChange={(e) => patchField(field.id, { key: keyFromLabel(e.target.value) })}
              />
            </label>
            <Input
              size="sm"
              className="w-full"
              placeholder="Help text / description (optional)"
              value={field.description ?? ''}
              onChange={(e) => patchField(field.id, { description: e.target.value || undefined })}
            />
          </div>
        )}

        {/* logic composer */}
        {logicComposerFor === field.id && (
          <div className="border-t border-border/30 px-10 py-3">
            <LogicComposer
              field={field}
              fieldKeys={fieldKeys}
              onAddRule={(when) => patchField(field.id, { requirements: [...(field.requirements ?? []), { when, effects: [] }] })}
              onAddVisibility={(when) => patchField(field.id, { visibleWhen: [...(field.visibleWhen ?? []), when] })}
              onDone={() => setLogicComposerFor(null)}
            />
          </div>
        )}

        {/* logic rule rows */}
        {(field.requirements ?? []).map((rule, ri) => {
          const subject = rule.when.field === field.key ? 'answer' : refName(rule.when.field);
          const op = opLabel(rule.when.op);
          const value = rule.when.value !== undefined ? String(rule.when.value) : '';
          const nestedForRule = children.filter((c) => (c.visibleWhen ?? []).some((cond) => condEq(cond, rule.when)));
          return (
            <div
              key={`req-${ri}`}
              className="flex flex-wrap items-center gap-2 border-t border-border/20 py-2 pl-14 pr-4 text-xs"
              style={{ boxShadow: 'inset 3px 0 0 0 #f59e0b' }}
            >
              <span className="text-muted-foreground">If {subject}</span>
              <span className="text-muted-foreground">{op}</span>
              {value && (
                <OptionChip
                  small
                  option={{ label: value, color: field.options?.find((o) => o.label === value)?.color ?? '#f59e0b' }}
                />
              )}
              <span className="text-muted-foreground">then</span>
              {nestedForRule.length > 0 && (
                <span className="inline-flex items-center gap-1 rounded-md border border-primary/40 px-2 py-0.5 font-semibold text-primary">
                  💬 Ask questions ({nestedForRule.length})
                  <Button
                    type="button"
                    variant="ghost"
                    size="fit"
                    className="hover:text-destructive"
                    title="Un-nest these questions"
                    onClick={() => update((d) => {
                      const s = d.sections.find((x) => x.id === section.id);
                      for (const c of s?.fields ?? []) {
                        if (nestedForRule.some((n) => n.id === c.id)) {
                          c.visibleWhen = (c.visibleWhen ?? []).filter((cond) => !condEq(cond, rule.when));
                          if (c.visibleWhen.length === 0) delete c.visibleWhen;
                        }
                      }
                    })}
                  >
                    ✕
                  </Button>
                </span>
              )}
              {rule.effects.map((eff) => (
                <span key={eff} className="inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-2 py-0.5 font-semibold text-amber-600 dark:text-amber-400">
                  {effectLabel(eff)}
                  <Button
                    type="button"
                    variant="ghost"
                    size="fit"
                    className="hover:text-destructive"
                    onClick={() => {
                      // Rules may keep zero effects — they can still anchor nested questions.
                      const requirements = (field.requirements ?? [])
                        .map((r, j) => (j === ri ? { ...r, effects: r.effects.filter((x) => x !== eff) } : r));
                      patchField(field.id, { requirements });
                    }}
                    aria-label="Remove trigger"
                  >
                    ✕
                  </Button>
                </span>
              ))}
              <span className="relative">
                <Button
                  type="button"
                  variant="ghost"
                  size="fit"
                  className="rounded border border-dashed border-amber-500/60 px-1.5 py-0.5 text-label font-semibold text-amber-600 hover:bg-amber-500/10 dark:text-amber-400"
                  onClick={(e) => setTriggerMenuFor(
                    triggerMenuFor?.fieldId === field.id && triggerMenuFor.ruleIndex === ri
                      ? null
                      : { fieldId: field.id, ruleIndex: ri, anchor: e.currentTarget },
                  )}
                >
                  + trigger
                </Button>
                {triggerMenuFor?.fieldId === field.id && triggerMenuFor.ruleIndex === ri && (
                  <TriggerMenu
                    anchor={triggerMenuFor.anchor}
                    existing={rule.effects}
                    onPick={(kind) => applyTrigger(field, section, ri, kind)}
                    onClose={() => setTriggerMenuFor(null)}
                  />
                )}
              </span>
              <IconButton
                icon={<TrashIcon className="h-3.5 w-3.5" />}
                variant="ghost"
                size="sm"
                className="ml-auto text-muted-foreground hover:text-destructive"
                title="Delete rule"
                aria-label="Delete rule"
                onClick={() => patchField(field.id, { requirements: (field.requirements ?? []).filter((_, j) => j !== ri) })}
              />
            </div>
          );
        })}

        {/* visibility rules that are NOT the nesting condition */}
        {(field.visibleWhen ?? [])
          .filter((cond) => !nestParent || !(cond.field === nestParent.key || cond.field === nestParent.id))
          .map((cond, ci) => (
            <div
              key={`vis-${ci}`}
              className="flex flex-wrap items-center gap-2 border-t border-border/20 py-2 pl-14 pr-4 text-xs"
              style={{ boxShadow: 'inset 3px 0 0 0 #8b5cf6' }}
            >
              <span className="text-muted-foreground">
                Shown when <span className="font-semibold">{refName(cond.field)}</span> {opLabel(cond.op)}
              </span>
              {cond.value !== undefined && <OptionChip small option={{ label: String(cond.value), color: '#8b5cf6' }} />}
              <Button
                type="button"
                variant="ghost"
                size="fit"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => patchField(field.id, {
                  visibleWhen: field.visibleWhen?.filter((c) => c !== cond),
                })}
                aria-label="Remove visibility rule"
              >
                ✕
              </Button>
            </div>
          ))}

        {/* nested follow-up questions (recursive, cycle-guarded) */}
        {children.map((child) => renderFieldRow(section, child, depth + 1, new Set([...visited, field.id])))}
      </div>
    );
  };

  // ------------------------------------------------------------------
  const modalField = responseModal ? findFieldById(responseModal.fieldId) : undefined;
  const sliderField = sliderModal ? findFieldById(sliderModal) : undefined;

  return (
    <div className="relative space-y-6" data-agent-target="form-builder">
      {/* Add rail — docks beside the selected row (falls back to mid-screen) */}
      {floatingAddRail && (
        <div
          className="fixed left-3 z-20 hidden flex-col overflow-hidden rounded-xl border border-border bg-background/95 shadow-lg backdrop-blur transition-[top] duration-200 lg:flex"
          style={{ top: railTop ?? Math.round(window.innerHeight / 3) }}
        >
          <Button
            type="button"
            variant="ghost"
            size="fit"
            className="focus-ring flex flex-col items-center gap-1 px-3 py-2.5 text-caption font-semibold text-muted-foreground hover:text-primary"
            onClick={() => {
              const sel = selected ? findFieldById(selected) : undefined;
              const section = sel
                ? schema.sections.find((s) => s.fields.some((f) => f.id === sel.id))
                : undefined;
              addQuestion({ sectionId: section?.id, afterId: sel?.id });
            }}
            title="Add question below the selected row"
          >
            <PlusIcon className="h-4 w-4" />
            Question
          </Button>
          <div className="border-t border-border/60" />
          <Button
            type="button"
            variant="ghost"
            size="fit"
            className="focus-ring flex flex-col items-center gap-1 px-3 py-2.5 text-caption font-semibold text-muted-foreground hover:text-primary"
            onClick={() => addSection('section')}
          >
            <Squares2X2Icon className="h-4 w-4" />
            Section
          </Button>
        </div>
      )}

      {schema.sections.map((section, si) => (
        <section
          key={section.id}
          ref={(element) => {
            if (element) sectionRefs.current.set(section.id, element);
            else sectionRefs.current.delete(section.id);
          }}
          data-template-section-id={section.id}
          className={`rounded-hairline border border-border bg-card overflow-visible ${activeSectionId === section.id ? 'ring-1 ring-primary/30' : ''}`}
          onClick={(event) => {
            if ((event.target as HTMLElement).closest('[data-template-field-id]')) return;
            setSelected(null);
            setActiveSectionId(section.id);
            onFocusChange?.(section.id, null);
          }}
          onDragOver={allowDrop}
          onDrop={(e) => {
            e.stopPropagation();
            if (dragRef.current?.kind === 'section') onDropSection(si);
            else onDropField(section.id, section.fields.length);
          }}
        >
          {/* 2-146e finding: this row had no `flex-wrap` and the name `<input>`
              was `flex-1` without `min-w-0` — at 390px the input refused to
              shrink and pushed the trailing "Delete section" trash button
              past the viewport's right edge entirely (confirmed live: the
              button rendered off-screen, unreachable by touch, not merely
              visually tight). Same class of fix as `TemplateEditorPage`'s
              own header in this story: `flex-wrap` + `min-w-0` on the one
              genuinely flexible child, `shrink-0` on the fixed-content
              siblings, so a narrow viewport wraps the row instead of
              clipping a control off-screen. No interaction/domain behavior
              changed — same drag handle, collapse toggle, rename input,
              repeat checkbox, count and delete button, only the row's own
              wrapping. */}
          <header className="flex flex-wrap items-center gap-2 px-4 py-3">
            <span
              {...handle({ kind: 'section', sectionId: section.id })}
              className="shrink-0 cursor-grab select-none text-muted-foreground"
              title="Drag to reorder section"
            >
              ⠿
            </span>
            <IconButton
              icon={collapsed[section.id] ? <ChevronRightIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
              variant="ghost"
              size="sm"
              className="shrink-0 text-muted-foreground"
              onClick={() => setCollapsed((p) => ({ ...p, [section.id]: !p[section.id] }))}
              aria-label={collapsed[section.id] ? 'Expand section' : 'Collapse section'}
            />
            <Input
              bare
              className="focus-ring min-w-0 flex-1 text-base font-semibold text-foreground"
              value={section.name}
              placeholder={section.kind === 'table' ? 'Table name' : section.kind === 'page' ? 'Page title' : 'Section name'}
              onChange={(e) => update((d) => {
                const s = d.sections.find((x) => x.id === section.id);
                if (s) s.name = e.target.value;
              })}
            />
            {section.kind === 'page' && (
              <span className="flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-caption font-semibold uppercase tracking-wide text-primary">
                <DocumentIcon className="h-3 w-3" /> Page
              </span>
            )}
            {section.kind === 'table' && (
              <span className="flex shrink-0 items-center gap-1 rounded-full bg-teal-500/10 px-2 py-0.5 text-caption font-semibold uppercase tracking-wide text-teal-500">
                <TableCellsIcon className="h-3 w-3" /> Table {schema.sections.filter((s) => s.kind === 'table').findIndex((s) => s.id === section.id) + 1}/{MAX_TABLES}
              </span>
            )}
            {section.kind !== 'table' && (
              <label
                className="flex shrink-0 items-center gap-1 text-label text-muted-foreground"
                title="Inspectors can duplicate this block per unit / asset while filling in"
              >
                <Checkbox
                  value={Boolean(section.repeat)}
                  onChange={(e) => update((d) => {
                    const s = d.sections.find((x) => x.id === section.id);
                    if (s) { if (e.target.checked) s.repeat = true; else delete s.repeat; }
                  })}
                />
                Repeat this section
              </label>
            )}
            {/* Story 4-110 — per-section, editable-after-creation page-flow
                control. Replaces the old creation-time-only `kind:'page'` +
                global `content.pageBreak` toggle double-gate: this is a
                REAL property on the section object, settable on any
                EXISTING section (any `kind`, including `table` — unlike the
                "Repeat this section" checkbox immediately above, which
                tables opt out of), not a one-time choice made in the "Add
                block" menu. See `contracts/reports.js
                #ReportContentSection.pageBreak`'s own comment for the full
                mechanism. A section that has never touched this control
                shows its resolved default — 'always' for a `kind:'page'`
                section (today's real behavior, unchanged), 'auto'
                otherwise — the SAME fallback every renderer/resolver in
                this family applies to a missing value. */}
            <span
              className="shrink-0"
              title="Page flow: start a fresh page, keep glued to the previous block, or flow normally"
            >
              <SegmentedControl<PageBreak>
                aria-label={`Page flow for ${section.name || 'section'}`}
                size="sm"
                value={section.pageBreak ?? (section.kind === 'page' ? 'always' : 'auto')}
                onChange={(next) => update((d) => {
                  const s = d.sections.find((x) => x.id === section.id);
                  if (s) s.pageBreak = next;
                })}
                options={PAGE_BREAK_OPTIONS}
              />
            </span>
            <span className="shrink-0 text-label text-muted-foreground">
              {section.fields.length} {section.kind === 'table' ? 'columns' : 'questions'}
            </span>
            <IconButton
              icon={<TrashIcon className="h-4 w-4" />}
              variant="ghost"
              size="sm"
              className="shrink-0 text-muted-foreground hover:text-destructive"
              onClick={() => update((d) => { d.sections = d.sections.filter((s) => s.id !== section.id); })}
              aria-label="Delete section"
            />
          </header>

          {!collapsed[section.id] && (
            <div className="border-t border-border/40">
              <div className="flex items-center justify-between px-4 py-2 text-label font-semibold uppercase tracking-wide text-muted-foreground">
                <span className="pl-6">{section.kind === 'table' ? 'Column' : 'Question'}</span>
                <span className="flex w-56 items-center justify-between pr-1">
                  Type of response
                  <IconButton
                    icon={<PlusIcon className="h-3.5 w-3.5" />}
                    variant="ghost"
                    shape="circle"
                    size="sm"
                    className="border border-border text-muted-foreground hover:text-primary"
                    onClick={() => addQuestion({ sectionId: section.id })}
                    aria-label="Add question"
                  />
                </span>
              </div>

              {section.fields
                .filter((f) => !parentOf(section, f))
                .map((field) => renderFieldRow(section, field, 0, new Set([field.id])))}

              <div className="border-t border-border/30 px-4 py-3">
                <Button
                  type="button"
                  variant="secondary"
                  size="fit"
                  className="flex items-center gap-1 text-xs font-semibold"
                  icon={<PlusIcon className="h-3.5 w-3.5" />}
                  onClick={(e) => setAddMenu({ anchor: e.currentTarget, sectionId: section.id })}
                >
                  Add new
                </Button>
              </div>
            </div>
          )}
        </section>
      ))}

      <div
        onDragOver={allowDrop}
        onDrop={(e) => { e.stopPropagation(); onDropSection(schema.sections.length); }}
      >
        <Button
          type="button"
          variant="secondary"
          size="fit"
          className="focus-ring text-sm font-semibold"
          onClick={(e) => setAddMenu({ anchor: e.currentTarget, sectionId: null })}
        >
          + Add block
        </Button>
      </div>

      {addMenu && (
        <AddNewMenu
          anchor={addMenu.anchor}
          hideQuestion={addMenu.sectionId === null}
          onPick={(kind) => {
            if (kind === 'question') addQuestion({ sectionId: addMenu.sectionId });
            else addSection(kind);
          }}
          onClose={() => setAddMenu(null)}
        />
      )}

      <RelationsEditor
        relations={schema.relations}
        fieldKeys={fieldKeys}
        onChange={(relations) => update((d) => { d.relations = relations; })}
      />

      {responseModal && modalField && (
        <ResponseSetModal
          initial={responseModal.options}
          onSave={(options) => patchField(modalField.id, {
            type: modalField.type === 'multiselect' ? 'multiselect' : 'select',
            options,
          })}
          onClose={() => setResponseModal(null)}
        />
      )}
      {sliderModal && sliderField && (
        <SliderModal
          initial={{ min: sliderField.min, max: sliderField.max, step: sliderField.step }}
          onSave={({ min, max, step }) => patchField(sliderField.id, { type: 'rating', min, max, step })}
          onClose={() => setSliderModal(null)}
        />
      )}
    </div>
  );
}
